import type { ColumnConfiguration, FieldConfiguration } from '../../types';

/**
 * PD-17066 — Competitors list columns, several values stacked per cell with
 * line breaks (the vendor list's pattern). Rows are `wholesaler` records plus
 * the page-only values the API adds (our sales, top product groups) and the
 * display values `toCompetitorListDisplayRow` joins.
 */
const TABLE = 'wholesaler';

const lineBreak = (
	id: string,
	visibleWhen?: FieldConfiguration['conditionalVisibility'],
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
	...(visibleWhen != null ? { conditionalVisibility: visibleWhen } : {}),
});

const secondaryText = (
	id: string,
	fieldName: string,
	visibleWhen?: FieldConfiguration['conditionalVisibility'],
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: TABLE } },
	styleConfig: {
		size: 'sm',
		colorRule: { type: 'theme', themeColor: 'textSecondary' },
	},
	displayStyle: 'text',
	...(visibleWhen != null ? { conditionalVisibility: visibleWhen } : {}),
});

const labelBadge = (
	id: string,
	fieldName: string,
	label: string,
	colorType: 'info' | 'neutral' | 'warning',
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: TABLE } },
	styleConfig: {
		size: 'xs',
		valueMapping: [{ value: label, label, color: { type: colorType } }],
	},
	displayStyle: 'badge',
	floatingLeft: true,
	conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
});

/** A short label in front of a value on the same line (only text fields render `prefix`). */
const inlineLabel = (
	id: string,
	text: string,
	visibleWhen: FieldConfiguration['conditionalVisibility'],
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: text },
	styleConfig: {
		size: 'sm',
		colorRule: { type: 'theme', themeColor: 'textSecondary' },
	},
	displayStyle: 'text',
	conditionalVisibility: visibleWhen,
});

/**
 * Change against the prior 12 months, red when down and green when up, after
 * a "vs prior 12 mo" label.
 */
export const salesTrendFields = (
	id: string,
	fieldName: string,
	objectTable: string,
): Array<FieldConfiguration> => [
	inlineLabel(`${id}-label`, 'vs prior 12 mo', {
		field: fieldName,
		condition: 'is_not_null',
	}),
	{
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable } },
	styleConfig: {
		size: 'sm',
		colorRule: {
			type: 'range-mapping',
			rangeMapping: [
				{ min: -1000000, max: -0.005, color: { bg: '', text: 'red' } },
				{ min: 0.005, max: 1000000, color: { bg: '', text: 'green' } },
			],
		},
		formatOptions: {
			numberFormat: 'percent',
			decimalPlaces: 0,
			percentValueIsWholeNumber: false,
		},
	},
	displayStyle: 'number',
	conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
	},
];

export const competitorNameColumn = (): ColumnConfiguration => ({
	id: 'competitor-name',
	fields: [
		{
			id: 'competitor-name-field',
			link: { type: 'object', enabled: true, objectTable: TABLE, idField: 'id' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: TABLE },
			},
			styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		lineBreak('competitor-name-lb1', {
			field: 'aliasesText',
			condition: 'is_not_null',
		}),
		secondaryText('competitor-aliases', 'aliasesText', {
			field: 'aliasesText',
			condition: 'is_not_null',
		}),
		lineBreak('competitor-name-lb2'),
		labelBadge('competitor-also-vendor', 'alsoVendorLabel', 'Also our vendor', 'info'),
		labelBadge('competitor-inactive', 'inactiveLabel', 'Inactive', 'neutral'),
	],
	headers: [{ id: 'h-competitor', text: 'Competitor' }],
	minWidth: 200,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'name', objectTable: TABLE }],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

export const competitorContactColumn = (): ColumnConfiguration => ({
	id: 'competitor-contact',
	fields: [
		{
			id: 'competitor-website',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'website', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		lineBreak('competitor-contact-lb1', {
			field: 'contactLine',
			condition: 'is_not_null',
		}),
		secondaryText('competitor-contact-line', 'contactLine', {
			field: 'contactLine',
			condition: 'is_not_null',
		}),
	],
	headers: [{ id: 'h-contact', text: 'Contact' }],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'ASC', fieldName: 'state', objectTable: TABLE },
		{ direction: 'ASC', fieldName: 'city', objectTable: TABLE },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

export const competitorCustomersColumn = (): ColumnConfiguration => ({
	id: 'competitor-customers',
	fields: [
		{
			id: 'competitor-customer-count',
			dataConfig: {
				type: 'template',
				// The header says Customers; the bare count avoids "1 customers".
				template: '{wholesaler.customerCount}',
				templateFields: [],
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		lineBreak('competitor-customers-lb1'),
		{
			id: 'competitor-relationship-counts',
			dataConfig: {
				type: 'template',
				template:
					'{wholesaler.primaryCount} primary · {wholesaler.secondaryCount} secondary · {wholesaler.alsoBuysCount} also',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-customers', text: 'Customers' }],
	// Wide enough for the relationship line ("2 primary · 1 secondary · 3 also").
	// Only a large-preset column keeps its minWidth when the table is squeezed
	// (computeShrinkFloor); a medium one shrinks to its header and cuts the line.
	minWidth: 240,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'DESC', fieldName: 'customerCount', objectTable: TABLE }],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

export const competitorSpendColumn = (): ColumnConfiguration => ({
	id: 'competitor-spend',
	fields: [
		{
			id: 'competitor-estimated-spend',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'estimatedAnnualSpend', objectTable: TABLE },
				defaultValue: 'Not estimated',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
			},
			displayStyle: 'currency',
		},
		lineBreak('competitor-spend-lb1', {
			field: 'estimatedAnnualSpend',
			condition: 'is_not_null',
		}),
		{
			id: 'competitor-estimated-on',
			dataConfig: {
				type: 'template',
				template:
					'estimated on {wholesaler.estimatedCustomerCount} of {wholesaler.customerCount} customers',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'estimatedAnnualSpend',
				condition: 'is_not_null',
			},
		},
	],
	headers: [{ id: 'h-spend', text: 'Their Business With Our Customers' }],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'DESC', fieldName: 'estimatedAnnualSpend', objectTable: TABLE },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/** Computed for the visible page only, so it cannot be sorted. */
export const competitorOurSalesColumn = (): ColumnConfiguration => ({
	id: 'competitor-our-sales',
	fields: [
		{
			id: 'competitor-our-sales-12',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'ourSales12Months', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
			},
			displayStyle: 'currency',
		},
		lineBreak('competitor-our-sales-lb1', {
			field: 'ourSalesTrend',
			condition: 'is_not_null',
		}),
		...salesTrendFields('competitor-our-sales-trend', 'ourSalesTrend', TABLE),
	],
	headers: [{ id: 'h-our-sales', text: 'Our Sales To Those Customers' }],
	minWidth: 170,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

export const competitorPricingColumn = (): ColumnConfiguration => ({
	id: 'competitor-pricing',
	fields: [
		{
			id: 'competitor-price-counts',
			dataConfig: {
				type: 'template',
				template:
					'{wholesaler.priceCount} prices on {wholesaler.pricedItemCount} items',
				templateFields: [],
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		lineBreak('competitor-pricing-lb1', {
			field: 'lastPriceDate',
			condition: 'is_not_null',
		}),
		inlineLabel('competitor-last-price-label', 'last logged', {
			field: 'lastPriceDate',
			condition: 'is_not_null',
		}),
		{
			id: 'competitor-last-price',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastPriceDate', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'relative_date',
			conditionalVisibility: { field: 'lastPriceDate', condition: 'is_not_null' },
		},
	],
	headers: [{ id: 'h-pricing', text: 'Pricing' }],
	minWidth: 200,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'DESC', fieldName: 'priceCount', objectTable: TABLE }],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

export const competitorProductGroupsColumn = (): ColumnConfiguration => ({
	id: 'competitor-product-groups',
	fields: [
		{
			id: 'competitor-top-product-groups',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'topProductGroups', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'xs', colorRule: { type: 'default' } },
			displayStyle: 'badge_list',
		},
		lineBreak('competitor-product-groups-lb1', {
			field: 'moreProductGroupsLabel',
			condition: 'is_not_null',
		}),
		secondaryText('competitor-more-product-groups', 'moreProductGroupsLabel', {
			field: 'moreProductGroupsLabel',
			condition: 'is_not_null',
		}),
	],
	headers: [{ id: 'h-product-groups', text: 'Product Groups' }],
	minWidth: 180,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

export const competitorLastConfirmedColumn = (): ColumnConfiguration => ({
	id: 'competitor-last-confirmed',
	fields: [
		{
			id: 'competitor-last-confirmed-date',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastConfirmedDate', objectTable: TABLE },
				defaultValue: 'Never',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'relative_date',
		},
		lineBreak('competitor-last-confirmed-lb1', {
			field: 'lastConfirmedByName',
			condition: 'is_not_null',
		}),
		secondaryText('competitor-last-confirmed-by', 'lastConfirmedByName', {
			field: 'lastConfirmedByName',
			condition: 'is_not_null',
		}),
	],
	headers: [{ id: 'h-last-confirmed', text: 'Last Confirmed' }],
	minWidth: 140,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'DESC', fieldName: 'lastConfirmedDate', objectTable: TABLE },
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

export const createDefaultCompetitorColumns = (): Array<ColumnConfiguration> => [
	competitorNameColumn(),
	competitorContactColumn(),
	competitorCustomersColumn(),
	competitorSpendColumn(),
	competitorOurSalesColumn(),
	competitorPricingColumn(),
	competitorProductGroupsColumn(),
	competitorLastConfirmedColumn(),
];
